import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { MIC_PERMISSION_OVERLAY_STYLES } from '../../components/mic-permission-overlay/mic-permission-overlay.styles';
import { EventManagerService } from '../event-manager/event-manager.service';
import type { VoiceInputTiming } from '../turn-latency/turn-latency.service';

/**
 *
 */
@Injectable({ providedIn: 'root' })
export class AudioToTextService {
  //public state: 'unknown' | 'connecting' | 'listening' | 'thinking' | 'sending' | 'disconnected' = 'unknown';
  public state = new BehaviorSubject<'unknown' | 'connecting' | 'listening' | 'thinking' | 'sending' | 'disconnected'>('unknown');
  public speakingState = new BehaviorSubject<boolean>(false);
  public micPermissionError = new BehaviorSubject<boolean>(false);
  public textReceived = new Subject<string>();
  public autoMute = false;
  public downloadForDebug = false;
  public recStarted = new BehaviorSubject<boolean>(false);
  public waitForSpeechInputEnd = false;
  private mediaRecorder!: MediaRecorder;
  private analyser!: AnalyserNode;
  public volumeScale = 1;
  public volumeScalePercentage = 0; // 0-100
  private audioCtx!: AudioContext;
  public forceMute = false;
  private dataArray: Uint8Array = new Uint8Array(2048);
  private silenceTimer: unknown = null;
  public silenceProgress = 0; // 0–100%
  private silenceStartTime: number | null = null;
  private speechEndAt: number | null = null;
  /** Zeitpunkte (performance.now) der letzten Spracheingabe; gesetzt unmittelbar vor textReceived. */
  public lastVoiceTiming: VoiceInputTiming | null = null;
  public chunks: Blob[] = [];
  private recordingSessionId = 0;
  public silenceThreshold = 0.02; // nachdem es laut war  (der user redet) wird die Aufnahme gestoppt und gesendet
  public silenceDelay = 1500; // wie lange es leise sein muss, bevor die Aufnahme gestoppt / gesendet wird
  public speechRecognitionThresholdBase = 0.03; // sobald der wert überschritten wird, wird geht der bot in den "beim nächsten mal leise soll ich senden modus"
  public speechRecognitionThresholdElevationMultiplyer = 1; // wenn der Bot redet, wird speechRecognitionThreshold % erhöht
  public animationFrameId: number = -1;
  public isMuted: boolean = false;
  public rootMeanSquare = 0;
  private firstConnection = true;

  constructor
    (
      private http: HttpClient,
      private eventManager: EventManagerService,
      private botIdService: BotidService
    ) {
    this.isMuted = this.autoMute;
  }

  /**
   *
   */
  public startRecording() {
    if (this.state.getValue() === 'listening') {
      return;
    }

    // Alten Recorder sauber abräumen, damit stale Handler keine Chunks (ohne EBML-Header) in die neue Session pushen.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.ondataavailable = null;
      this.mediaRecorder.onstop = null;
      try { this.mediaRecorder.stop(); } catch { /* already stopped */ }
    }

    this.recordingSessionId++;
    const sessionId = this.recordingSessionId;

    this.waitForSpeechInputEnd = false;
    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
      if (sessionId !== this.recordingSessionId) {
        // Eine neuere startRecording()-Session hat diese bereits abgelöst.
        stream.getTracks().forEach(t => { t.stop(); });
        return;
      }
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.AUDIO.SPEECH.recording", { stream });

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = this.audioCtx.createMediaStreamSource(stream);
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 2048;
      this.dataArray = new Uint8Array(this.analyser.fftSize);
      source.connect(this.analyser);
      this.chunks = [];
      this.mediaRecorder = new MediaRecorder(stream);

      this.mediaRecorder.ondataavailable = (e) => {
        if (sessionId !== this.recordingSessionId) return;
        if (this.state.getValue() !== 'listening') {
          this.state.next('listening');
        }
        if (!this.isMuted || this.chunks.length === 0) {
          this.chunks.push(e.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        if (sessionId !== this.recordingSessionId) return;
        void this.onRecordingStopped();
        this.recStarted.next(false);
      };

      this.mediaRecorder.start(500);
      if (this.firstConnection) {
        this.eventManager._trigger_RAGAI_EVENT("RAGAI.AUDIO.SPEECH.micconnected", { stream });
        this.firstConnection = false;
      }

      this.state.next('listening');
      if (this.animationFrameId == -1) {
        this.checkSilence();
      }
    }).catch((err: unknown) => {
      if (this.isInFusionView()) {
        this.showMicPermissionOverlayInParent();
      } else {
        this.micPermissionError.next(true);
      }
      console.error('Mic permission error:', err);
    });
  }

  /**
   * Bot läuft eingebettet in einer Fusion-View (LMS-Kursseite) – der eigene position:fixed-Overlay würde nur das (oft kleine/versteckte) Bot-Iframe abdecken.
   * @returns `true`, wenn der Bot in einer Fusion-View eingebettet läuft.
   */
  private isInFusionView(): boolean {
    try {
      return window.parent !== window && !!(window.parent as Window & { fusionmeta?: unknown }).fusionmeta;
    } catch {
      return false;
    }
  }

  /** Zeigt den Mikrofon-Hinweis direkt im Elternfenster (Fusion-View-Kursseite), analog zu showGDPRInParent() in bot-based.component.ts. */
  private showMicPermissionOverlayInParent(): void {
    if (parent.document.getElementById('ragai-mic-permission-overlay')) return;

    if (!parent.document.getElementById('ragai-mic-permission-overlay-style')) {
      const style = parent.document.createElement('style');
      style.id = 'ragai-mic-permission-overlay-style';
      style.textContent = MIC_PERMISSION_OVERLAY_STYLES;
      parent.document.head.appendChild(style);
    }

    const overlay = parent.document.createElement('div');
    overlay.id = 'ragai-mic-permission-overlay';
    overlay.className = 'ragai-mic-permission-overlay';

    const content = parent.document.createElement('div');
    content.className = 'ragai-mic-permission-content';

    const icon = parent.document.createElement('span');
    icon.className = 'ragai-mic-permission-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '⚠️';

    const title = parent.document.createElement('h3');
    title.textContent = $localize`:@@mic-permission-title:Mikrofon nicht verfügbar`;

    const description = parent.document.createElement('p');
    description.className = 'ragai-mic-permission-description';
    description.textContent = $localize`:@@mic-permission-error:Dein Mikrofon konnte nicht erkannt werden. Prüfe, ob es angeschlossen und freigegeben ist, und lade die Seite neu.`;

    const hint = parent.document.createElement('div');
    hint.className = 'ragai-mic-permission-hint';
    const hintText = parent.document.createElement('span');
    hintText.textContent = $localize`:@@mic-permission-hint:Achte auf die Berechtigungsanfrage oben in deinem Browser und erlaube den Mikrofonzugriff.`;
    hint.append(hintText);

    const btn = parent.document.createElement('button');
    btn.className = 'ragai-mic-permission-btn';
    // Reload die Fusion-View-Kursseite (parent), nicht nur das Bot-Iframe - sonst bleibt dieser Overlay (er hängt im parent.document) für immer stehen.
    btn.onclick = () => { parent.location.reload(); };
    const btnIcon = parent.document.createElement('span');
    btnIcon.setAttribute('aria-hidden', 'true');
    btnIcon.textContent = '↻';
    const btnText = parent.document.createElement('span');
    btnText.textContent = $localize`:@@dc-overlay-default-continue:Seite neu laden`;
    btn.append(btnIcon, btnText);

    content.append(icon, title, description, hint, btn);
    overlay.appendChild(content);
    parent.document.body.appendChild(overlay);
  }

  public get speechRecognitionThreshold() {
    return this.speechRecognitionThresholdBase * this.speechRecognitionThresholdElevationMultiplyer;
  }

  // ON EVERY FRAME
  private checkSilence = () => {
    this.animationFrameId = requestAnimationFrame(this.checkSilence);

    if (this.state.getValue() === "sending") return;
    //TODO: Fix this
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.analyser.getByteTimeDomainData(this.dataArray);
    let sum = 0;
    for (const check of this.dataArray) {
      const val = (check - 128) / 128;
      sum += val * val;
    }
    this.rootMeanSquare = Math.sqrt(sum / this.dataArray.length);
    if (!this.isMuted && this.rootMeanSquare > this.speechRecognitionThreshold) {
      // Barge-in: Bot denkt nach/Avatar spricht noch - jetzt aufnehmen statt bis "idle" zu warten
      if (this.state.getValue() === 'thinking' && !this.recStarted.getValue()) {
        this.startRecording();
      }
      this.waitForSpeechInputEnd = true;
      if (!this.recStarted.getValue()) {
        this.recStarted.next(true);
      }
    }

    // NUR LAYOUT
    if (this.isMuted) {
      this.volumeScale = 1;
    } else {
      const clampedRMS = Math.min(this.rootMeanSquare, 0.2); // Begrenze Maximalwert
      this.volumeScale = 1 + clampedRMS * 5; // Skaliere visuell, z.B. 1.0 bis 2.0
    }
    if (this.state.getValue() !== 'listening') { this.volumeScale = 1; } // Wenn der Bot redet, setze den Wert auf 1

    // Convert volumeScale to a percentage between 0 and 100
    const maxVolumeScale = 2.0; // Assuming the maximum visual scale is 2.0
    this.volumeScale = Math.min(this.volumeScale, maxVolumeScale); // Clamp to maxVolumeScale
    this.volumeScalePercentage = ((this.volumeScale - 1) / (maxVolumeScale - 1)) * 100;

    const now = performance.now();
    if ((this.isMuted || this.rootMeanSquare < this.silenceThreshold) && this.waitForSpeechInputEnd) {
      if (!this.silenceTimer) {
        this.silenceStartTime = now;
        this.silenceTimer = setTimeout(() => {
          this.speechEndAt = this.silenceStartTime;
          this.mediaRecorder?.stop();
          this.silenceProgress = 0;
          this.silenceStartTime = null;
        }, this.silenceDelay);
      } else if (this.silenceStartTime !== null) {
        const elapsed = now - this.silenceStartTime;
        this.silenceProgress = Math.min((elapsed / this.silenceDelay) * 100, 100);
      }
    } else {
      clearTimeout(this.silenceTimer as number);
      this.silenceTimer = null;
      this.silenceStartTime = null;
      this.silenceProgress = 0;
    }
  };

  /**
   * Wandelt eine aufgenommene Audio-Datei per Backend-API in Text um.
   * @param blob Die aufgenommene Audio-Datei.
   * @returns Der erkannte Text und die erkannte Sprache.
   */
  public async getTextFromSpeech(blob: Blob): Promise<{ text: string; recognitionLanguage?: string }> {
    const formData = new FormData();
    formData.append('file', blob, "none.mp3");
    formData.append('botId', this.botIdService.getBotId());
    formData.append('locale', document.documentElement.lang.toLowerCase());
    if (this.downloadForDebug) {
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'none.mp3';
      a.click();
      URL.revokeObjectURL(downloadUrl); // Speicher freigeben
    }
    // POST an Backend
    return await firstValueFrom(
      this.http.post<{ text: string; recognitionLanguage?: string }>("/api/toolbox/speechtotext", formData)
    );
  }

  /**
   *
   */
  public async onRecordingStopped() {
    const submittedAt = performance.now();
    const speechEndAt = this.speechEndAt;
    this.speechEndAt = null;
    this.eventManager.triggerEventHook("RAGAI.AUDIO.SPEECH.recordingstopped", { chunks: this.chunks });
    let myWords: { text: string; recognitionLanguage?: string | undefined } = { text: "", recognitionLanguage: undefined };
    this.state.next('sending');
    // AUTOMATISCH MUTEN
    if (this.autoMute) {
      this.setMuted(true);
    }
    if (this.chunks.length > 1) {
      const blob = new Blob(this.chunks, { type: 'audio/webm' });
      this.chunks = [];
      this.eventManager.triggerEventHook("RAGAI.AUDIO.SPEECH.attRequest", { blob });
      try {
        myWords = await this.getTextFromSpeech(blob);
        this.eventManager.triggerEventHook("RAGAI.AUDIO.SPEECH.attRecive", { text: myWords.text, recognitionLanguage: myWords.recognitionLanguage });
      } catch (err) {
        // Ohne Fallback bliebe der Status fuer immer bei 'sending' ("Sende…") haengen
        console.error('[AudioToTextService] speechtotext request failed:', err);
      }
    }

    if (myWords.text === '') {
      this.startRecording();
    } else {
      this.lastVoiceTiming = { speechEndAt, submittedAt, sttDoneAt: performance.now() };
      this.textReceived.next(myWords.text);
      this.state.next('thinking');
    }
  }

  /**
   * Schaltet das Mikrofon stumm oder wieder ein.
   * @param muted `true` zum Stummschalten, `false` zum Aktivieren.
   */
  public setMuted(muted: boolean) {
    this.isMuted = muted;
    if (muted) {
      // Kann bereits 'inactive' sein (z.B. autoMute direkt aus onRecordingStopped() heraus, wo der
      // Recorder gerade erst durch sein eigenes onstop-Event inactive wurde) - stop() wuerde dann eine
      // InvalidStateError werfen und den restlichen Aufrufer-Code (Transkription/Senden) abbrechen,
      // wodurch der Status fuer immer bei 'sending' haengen bleibt.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        try { this.mediaRecorder.stop(); } catch { /* already stopped */ }
      }
      return;
    }
    // unmute: alten Recorder verwerfen und sofort neu starten. MediaRecorder.stop() setzt state
    // synchron auf 'inactive', startRecording() erkennt das ueber recordingSessionId zuverlaessig,
    // daher ist keine kuenstliche Wartezeit noetig (frueher: 3s Verzoegerung bis das Mikro zuhoerte).
    this.chunks = [];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try { this.mediaRecorder.stop(); } catch { /* already stopped */ }
    }
    this.startRecording();
  }
}
